import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import PageShell, { RefreshButton } from "../components/PageShell";
import ErrorState, { ErrorBanner } from "../components/ErrorState";
import { Skeleton } from "../components/LoadingSkeleton";
import ProgressBar from "../components/ProgressBar";
import DayTimeline, { HOUR_PX } from "../components/DayTimeline";
import UpcomingEvents from "../components/UpcomingEvents";
import StatusDot, { TONE_TEXT, type Tone } from "../components/StatusDot";
import TodoPanel from "../components/TodoPanel";
import { IconTrendDown, IconTrendFlat, IconTrendUp } from "../components/icons";
import { useApi } from "../hooks/useApi";
import { usePageRefresh } from "../hooks/usePageRefresh";
import { getSummary, getWellness } from "../lib/api";
import { clearDayCache } from "../lib/dayCache";
import type {
  BusinessSnapshot,
  DashboardSummary,
  ExternalContext,
  GoalsSnapshot,
  MoneySnapshot,
  SectionStatus,
  UpcomingDate,
  WellbeingEntry,
  WellnessOverview,
} from "../types";
import { GRID_FROM_MIN, GRID_TO_MIN } from "../lib/agenda";
import {
  delta as fmtDelta,
  minutes,
  money,
  num,
  pct,
  plural,
} from "../lib/format";
import { readinessTone } from "../lib/wellbeing";

/**
 * Home screen, the «Диспетчерська дня» layout — picked by Vitalik from three
 * Codex mockup rounds (2026-08-23) to replace the KPI band + rail page.
 *
 * 1. Hero — what to do, where the day's time goes, and what is coming: the
 *    Todoist list (4/12), the vertical hour grid of the chosen day (5/12) and
 *    the next five calendar events (3/12). The grid sets the row's height.
 * 2. Four state cards — Гроші (spendable + budget tone + charges + net worth),
 *    Бізнес (the LovyTalon pulse), Здоровʼя (readiness + the four
 *    figures it is made of), Цілі під загрозою (count + the worst three, and
 *    a hint tile for the worst of those).
 * 3. A context strip — USD/EUR and the weather, back on the page by request.
 *
 * Below `lg` the hero row is one column and «Найближчі події» is not drawn:
 * tasks, day, money, business, health, goals. The horizon column answers a
 * different question than the rest of the screen, and «Мій день» now opens the
 * whole week on its own.
 *
 * Data: GET /api/summary, plus one unpolled read — GET /api/wellness/overview
 * for the readiness card.
 */
export default function OverviewPage() {
  const summary = useApi<DashboardSummary>(getSummary, [], { pollMs: 120_000 });
  const wellness = useApi<WellnessOverview>(() => getWellness(14), []);
  const data = summary.data;

  // A degraded payload retries quickly instead of waiting out the two-minute
  // poll: the slow source usually has a fresh answer seconds later, and the
  // banner should not outlive the outage.  Throttled so a source that is down
  // for the evening gets the ordinary poll, not a hammering.
  const lastFastRetry = useRef(0);
  const refetchSummary = summary.refetch;
  useEffect(() => {
    if (!data) return;
    const degraded =
      (data.sections ?? []).some((s) => s.status !== "ok") ||
      (data.degraded_sources ?? []).length > 0;
    if (!degraded) return;
    if (Date.now() - lastFastRetry.current < 60_000) return;
    lastFastRetry.current = Date.now();
    const timer = setTimeout(() => refetchSummary(), 12_000);
    return () => clearTimeout(timer);
  }, [data, refetchSummary]);

  // «Оновити» means "re-read this screen from the sources": the summary, the
  // Todoist panel (its own query, refreshed by the same event) and the days
  // «Мій день» has walked through — the summary only carries today. Both grids
  // listen for the day cache emptying and go back to Google; every read that
  // follows goes past the server's cache (lib/refresh.ts).
  const { refresh, busy } = usePageRefresh([summary, wellness], clearDayCache);

  const today = data
    ? new Date(`${data.today}T00:00:00`).toLocaleDateString("uk-UA", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : "";

  return (
    <PageShell
      title={data?.greeting_ua ?? "Сьогодні"}
      subtitle={
        data ? (
          <>
            {today}
            <HeaderContext context={data.context} />
          </>
        ) : undefined
      }
      actions={<RefreshButton onClick={refresh} busy={busy} />}
      banner={
        <>
          {summary.error && data && (
            <ErrorBanner message={summary.error} onRetry={summary.refetch} />
          )}
          {data && <DegradedBanner data={data} />}
        </>
      }
    >
      {summary.loading && !data && <OverviewSkeleton />}

      {!data && summary.error && (
        <div className="jv-card p-3 sm:p-4">
          <ErrorState
            title="Дашборд не завантажився"
            message={summary.error}
            code={summary.code}
            onRetry={summary.refetch}
          />
        </div>
      )}

      {data && <OverviewContent data={data} wellness={wellness} />}
    </PageShell>
  );
}

/**
 * The loaded layout, extracted so the parity test can hold it against
 * `OverviewSkeleton` — the two must always describe the same structure
 * (Vitalik, 2026-08-21: «скелет ніколи не відстає»). Change a section here
 * and `OverviewPage.parity.test.tsx` fails until the skeleton learns it.
 */
export function OverviewContent({
  data,
  wellness,
}: {
  data: DashboardSummary;
  wellness: { data: WellnessOverview | null; error: string | null };
}) {
  // Subscription charges belong to the money card. Everything else that was
  // dated — holidays, goal deadlines — used to ride along with the day as a
  // «НАЙБЛИЖЧЕ» strip of badges; that strip is gone (2026-09-07), and its
  // successor column shows calendar events only.
  const charges = (data.context.upcoming_dates ?? []).filter(
    (d) => d.kind === "subscription" || d.kind === "payment",
  );

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Three unequal columns on a desktop: the list he acts on, the day he
          reads, the horizon he glances at. Below `lg` it is one column and only
          the first two of them: on a phone or a tablet the screen is answering
          «що робити зараз», and «Найближчі події» is the one card that answers
          a different question — it stays rendered and hidden rather than
          dropped, so the page keeps its shape (and its parity test). Every
          «Найближче» it would show is a swipe away in «Мій день» anyway, now
          that «На весь екран» opens the whole week. */}
      <div className="grid gap-5 sm:gap-6 lg:grid-cols-12 lg:items-stretch">
        <TodoPanel
          title="Задачі на сьогодні"
          className="lg:col-span-4"
          today={data.today}
        />

        <DayTimeline
          className="lg:col-span-5"
          today={data.today}
          available={data.context.calendar_available}
          events={data.context.calendar_today ?? []}
          note={data.context.calendar_note}
        />

        <UpcomingEvents
          className="hidden lg:flex lg:col-span-3"
          today={data.today}
          events={data.context.calendar_upcoming ?? []}
          available={data.context.calendar_available}
          note={data.context.calendar_note}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-6 xl:grid-cols-4">
        <MoneyCard money={data.money} charges={charges} />
        {data.business && <BusinessCard business={data.business} />}
        <HealthCard state={wellness} />
        <GoalsCard goals={data.goals} />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- blocks */

/** Card header: a title and the door into the tab it condenses. */
function CardHead({
  title,
  to,
  linkLabel,
}: {
  title: string;
  to: string;
  linkLabel: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-semibold text-fg">{title}</h2>
      <Link to={to} className="shrink-0 text-micro text-muted hover:text-fg">
        {linkLabel}
      </Link>
    </div>
  );
}

const BUDGET_UA = { ok: "ок", warn: "впритул", over: "перебір" } as const;

/**
 * Personal money only (business has its own card and its own colour). Leads
 * with the same spendable figure as Фінанси; when the overview is missing it
 * falls back to the account total and says so in the caption instead of
 * pretending the budget math happened.
 */
function MoneyCard({
  money: m,
  charges,
}: {
  money: MoneySnapshot;
  charges: UpcomingDate[];
}) {
  const tone = m.budget_tone;
  // No tone means no active budget (draft, or the overview failed) — then
  // «доступно до кінця місяця» is a computation that never happened, and the
  // honest hero is the account total, captioned as such.
  const budgeted = tone !== null && m.spendable_month_uah !== null;
  const barPct =
    m.budget_limit_uah && m.budget_limit_uah > 0 && m.budget_spent_uah !== null
      ? (m.budget_spent_uah / m.budget_limit_uah) * 100
      : null;

  return (
    <section
      className="jv-card flex flex-col gap-2.5 p-3 sm:p-4"
      aria-label="Гроші"
    >
      <CardHead title="Гроші" to="/finance" linkLabel="фінанси →" />

      <div>
        <div className="jv-num text-metric-lg font-semibold text-money">
          {money(
            budgeted ? (m.spendable_month_uah as number) : m.balance_total_uah,
          )}
        </div>
        <div className="text-xs text-muted">
          {budgeted ? "до кінця місяця" : "на рахунках"}
        </div>
      </div>

      {tone && (
        <div className="space-y-1.5">
          <div className="text-xs text-muted">
            Бюджет:{" "}
            <span className={TONE_TEXT[tone === "over" ? "danger" : tone]}>
              {BUDGET_UA[tone]}
            </span>
          </div>
          {barPct !== null && (
            <ProgressBar
              value={barPct}
              tone={tone === "over" ? "danger" : tone}
              height={5}
            />
          )}
        </div>
      )}

      {charges.length > 0 && (
        <div className="border-t border-line pt-2">
          <div className="mb-1 text-micro font-medium text-muted">
            НАЙБЛИЖЧІ СПИСАННЯ
          </div>
          <ul className="space-y-1">
            {charges.slice(0, 2).map((c) => (
              <li
                key={`${c.date}-${c.title}`}
                className="flex items-baseline justify-between gap-2 text-xs"
              >
                <span className="min-w-0 truncate text-fg" title={c.title}>
                  {c.title.replace(/^Списання /u, "")}
                </span>
                <span className="shrink-0 text-muted">
                  {c.amount_uah !== null && (
                    <>
                      <span className="jv-num">{money(c.amount_uah)}</span>
                      {" · "}
                    </>
                  )}
                  {c.days_left === 0 ? "сьогодні" : `через ${c.days_left} дн.`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.net_worth_uah !== null && (
        <div className="mt-auto border-t border-line pt-2">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-muted">Капітал</span>
            <span className="jv-num text-fg">{money(m.net_worth_uah)}</span>
          </div>
          {/* The second figure only when it says something different: what is
              his once every open debt settles (Vitalik, 2026-09-06). */}
          {m.settled_worth_uah !== null &&
            Math.abs(m.settled_worth_uah - m.net_worth_uah) >= 1 && (
              <div className="mt-0.5 flex items-baseline justify-between gap-2 text-micro text-muted">
                <span>з урахуванням боргів</span>
                <span className="jv-num">{money(m.settled_worth_uah)}</span>
              </div>
            )}
        </div>
      )}
    </section>
  );
}

/** The bot's pulse for today: what it took, who joined, how many it has. */
function BusinessCard({ business: b }: { business: BusinessSnapshot }) {
  const lt = b.lt_users_total !== null;
  return (
    <section
      className="jv-card flex flex-col gap-2.5 p-3 sm:p-4"
      aria-label="Бізнес"
    >
      <CardHead title="Бізнес" to="/business" linkLabel="бізнес →" />

      <div className="flex flex-1 flex-col">
        <div className="text-xs text-muted">LovyTalon · сьогодні</div>
        {lt ? (
          <>
            <div className="mt-0.5">
              <span className="jv-num text-metric-sm text-business">
                {money(b.lt_revenue_today_uah ?? 0)}
              </span>{" "}
              <span className="text-xs text-muted">
                ·{" "}
                <span className="jv-num">
                  {num(b.lt_revenue_today_stars ?? 0)}
                </span>{" "}
                {plural(
                  b.lt_revenue_today_stars ?? 0,
                  "зірка",
                  "зірки",
                  "зірок",
                )}
              </span>
            </div>
            <div className="text-xs text-muted">
              +<span className="jv-num">{num(b.lt_users_today ?? 0)}</span>{" "}
              {plural(
                b.lt_users_today ?? 0,
                "користувач",
                "користувачі",
                "користувачів",
              )}{" "}
              · всього{" "}
              <span className="jv-num">{num(b.lt_users_total ?? 0)}</span>
            </div>
          </>
        ) : (
          // «—» over an apology: the bot being unreachable is the business
          // page's story, this card only admits it has no number.
          <div className="jv-num text-metric-sm text-muted">—</div>
        )}
      </div>
    </section>
  );
}

/**
 * Readiness, then the four numbers it is made of. Reads its own unpolled
 * endpoint, so its failure says so instead of blanking the page that came
 * from `getSummary`.
 */
function HealthCard({
  state,
}: {
  state: { data: WellnessOverview | null; error: string | null };
}) {
  return (
    <section
      className="jv-card flex flex-col gap-2.5 p-3 sm:p-4"
      aria-label="Здоровʼя"
    >
      <CardHead title="Здоровʼя" to="/health" linkLabel="детальніше →" />
      {!state.data ? (
        state.error ? (
          <p className="text-xs text-danger">
            Здоровʼя не завантажилось: {state.error}
          </p>
        ) : (
          <div className="space-y-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-28" />
          </div>
        )
      ) : (
        <ReadinessReadout overview={state.data} />
      )}
    </section>
  );
}

function ReadinessReadout({ overview }: { overview: WellnessOverview }) {
  const readiness = overview.readiness;
  const score = readiness?.available ? readiness.score_pct : null;
  const rows = pickHealthRows(overview);

  return (
    <>
      {score === null ? (
        <p className="text-xs text-muted">
          {readiness?.note ?? "Немає даних готовності."}
        </p>
      ) : (
        <div>
          <div
            className={`jv-num text-metric ${TONE_TEXT[readinessTone(score)]}`}
          >
            {pct(score, 0)}
          </div>
          <div className="text-xs text-muted">
            готовність
            {readiness?.delta_pct != null && (
              <>
                {" · "}
                <span className="jv-num">
                  {fmtDelta(readiness.delta_pct, 0)}
                </span>{" "}
                за тиждень
              </>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <dl className="mt-auto space-y-1.5 border-t border-line pt-2">
          {rows.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <dt className="text-muted">{row.label}</dt>
              <dd className="flex items-center gap-1.5">
                <span className="jv-num text-fg">{row.value}</span>
                {row.suffix && <span className="text-muted">{row.suffix}</span>}
                <TrendArrow trend={row.trend} tone={row.tone} hint={row.hint} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}

/**
 * Which way the figure moved against its own 7-day baseline. The *glyph* is
 * the movement (higher / lower), the *colour* is the verdict — they disagree
 * on purpose for stress, where down is the good direction.
 */
function TrendArrow({
  trend,
  tone,
  hint,
}: {
  trend: Trend;
  tone: Tone;
  hint: string;
}) {
  const Glyph =
    trend === "up"
      ? IconTrendUp
      : trend === "down"
        ? IconTrendDown
        : IconTrendFlat;
  return (
    <span className={`inline-flex shrink-0 ${TONE_TEXT[tone]}`} title={hint}>
      <Glyph className="size-3.5" />
    </span>
  );
}

type Trend = "up" | "down" | "flat";

interface HealthRow {
  key: string;
  label: string;
  /** The figure, rendered mono. */
  value: string;
  /** Words that follow it, if the figure alone does not say enough. */
  suffix?: string;
  trend: Trend;
  tone: Tone;
  /** What the arrow means, for the pointer and for a screen reader. */
  hint: string;
}

const numberOr = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * A metric's own 7-day baseline from the check-in history, for the case where
 * the backend has not sent one. The newest day is excluded: comparing today
 * against an average that contains today flattens every arrow.
 */
function baselineOf(
  entries: WellbeingEntry[],
  key: "stress" | "energy",
): number | null {
  const history = [...entries]
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(0, -1)
    .slice(-7)
    .map((entry) => numberOr(entry[key]))
    .filter((value): value is number => value !== null);
  return history.length > 0
    ? history.reduce((a, b) => a + b, 0) / history.length
    : null;
}

/** Movement against the baseline, and whether that movement is good news. */
function trendOf(
  latest: number | null,
  baseline: number | null,
  epsilon: number,
  lowerIsBetter: boolean,
): { trend: Trend; tone: Tone; hint: string } {
  if (latest === null || baseline === null) {
    return { trend: "flat", tone: "muted", hint: "Немає з чим порівняти" };
  }
  if (Math.abs(latest - baseline) < epsilon) {
    return {
      trend: "flat",
      tone: "muted",
      hint: "На рівні середнього за 7 днів",
    };
  }
  const higher = latest > baseline;
  const better = lowerIsBetter ? !higher : higher;
  return {
    trend: higher ? "up" : "down",
    tone: better ? "ok" : "warn",
    hint: better ? "Краще за середнє за 7 днів" : "Гірше за середнє за 7 днів",
  };
}

/**
 * The wellness overview as the four rows this card draws.
 *
 * One adapter rather than four expressions inline, because each row has a
 * fallback for the day its own field comes back null — the Apple Health
 * `sleep_hours` series, today's check-in, the rolling seven days of Hevy — and
 * those fallbacks are the difference between a card that reads and a card of
 * dashes. A row with no value at all does not render: an absent row beats a
 * confident lie (DESIGN.md §6).
 */
export function pickHealthRows(overview: WellnessOverview): HealthRow[] {
  const rows: HealthRow[] = [];

  // Сон — minutes. Falls back to the Apple Health `sleep_hours` series, which
  // is where the same number lived before the field existed (hours, so ×60).
  const nights = (overview.body ?? []).find(
    (series) => series.metric === "sleep_hours",
  );
  const sleep =
    numberOr(overview.sleep_minutes) ??
    (nights?.latest != null ? nights.latest * 60 : null);
  const sleepBaseline =
    numberOr(overview.sleep_minutes_avg_7d) ??
    (nights?.avg != null ? nights.avg * 60 : null);
  if (sleep !== null) {
    rows.push({
      key: "sleep",
      label: "Сон",
      value: minutes(sleep),
      ...trendOf(sleep, sleepBaseline, 10, false),
    });
  }

  // Стрес / Енергія — the 1..10 check-in scales. Stress is inverted, the same
  // way `lib/wellbeing.ts` treats it.
  const checkin = overview.wellbeing_today;
  const stress = numberOr(overview.stress) ?? numberOr(checkin?.stress);
  if (stress !== null) {
    rows.push({
      key: "stress",
      label: "Стрес",
      value: `${stress}/10`,
      ...trendOf(
        stress,
        numberOr(overview.stress_avg_7d) ??
          baselineOf(overview.wellbeing ?? [], "stress"),
        0.5,
        true,
      ),
    });
  }

  // Тренування — the ISO week against the week template. Without
  // `workouts_this_week` the rolling seven days is the honest stand-in, and
  // the caption owns up to which one is on screen.
  const thisWeek = numberOr(overview.workouts_this_week);
  const training = overview.training;
  const done =
    thisWeek ?? (training?.available ? training.workouts_last_7d : null);
  const plan = numberOr(overview.workouts_plan_per_week);
  if (done !== null) {
    const baseline =
      training?.available && training.workouts_last_30d > 0
        ? (training.workouts_last_30d * 7) / 30
        : null;
    rows.push({
      key: "training",
      label: "Тренування",
      value: plan && plan > 0 ? `${done}/${plan}` : `${done}`,
      suffix:
        plan && plan > 0
          ? undefined
          : thisWeek !== null
            ? "цього тижня"
            : "за 7 дн.",
      ...trendOf(done, baseline, 0.5, false),
    });
  }

  const energy = numberOr(overview.energy) ?? numberOr(checkin?.energy);
  if (energy !== null) {
    rows.push({
      key: "energy",
      label: "Енергія",
      value: `${energy}/10`,
      ...trendOf(
        energy,
        numberOr(overview.energy_avg_7d) ??
          baselineOf(overview.wellbeing ?? [], "energy"),
        0.5,
        false,
      ),
    });
  }

  return rows;
}

/**
 * Where the period's goals stand.
 *
 * This card used to be «Цілі під загрозою»: a count of the goals lagging the
 * elapsed share of their period, worst three listed with days left. Vitalik cut
 * the pace verdict on 2026-09-20 — a year goal is behind its clock every day
 * until the week it isn't, so the card spent the year shouting. What replaced
 * it is the thing the goals themselves say: how far along they are, and how
 * many are closed.
 */
function GoalsCard({ goals: g }: { goals: GoalsSnapshot }) {
  const { done_goals: done, total_goals: total } = g;
  const allDone = total > 0 && done === total;

  return (
    <section className="jv-card flex flex-col gap-2.5 p-3 sm:p-4" aria-label="Цілі">
      <CardHead title="Цілі" to="/goals" linkLabel="усі цілі →" />
      {total === 0 ? (
        <div>
          <div className="jv-num text-metric text-muted">—</div>
          <div className="text-xs text-muted">на цей період цілей ще немає</div>
        </div>
      ) : (
        <>
          <div>
            <div className={`jv-num text-metric ${allDone ? "text-ok" : "text-fg"}`}>
              {pct(g.overall_progress_pct, 0)}
            </div>
            <div className="text-xs text-muted">
              прогрес · <span className="jv-num">{done}</span> з{" "}
              <span className="jv-num">{total}</span> закрито
            </div>
          </div>
          {/* 5px, like the budget bar on «Гроші» — the two sit in one row. */}
          <ProgressBar
            value={g.overall_progress_pct}
            tone={allDone ? "ok" : "accent"}
            height={5}
          />
        </>
      )}
    </section>
  );
}

/**
 * Ambient context — USD/EUR and the weather — on the greeting line itself.
 * It lived a year off «Сьогодні», came back as a bottom strip (2026-08-23
 * grilling) and moved up here next to «Доброго дня» on 2026-08-28, phone
 * excluded entirely on the same call: on a 390px screen the trivia is the
 * first thing that has to go, and the date alone earns the second line.
 */
function HeaderContext({ context }: { context: ExternalContext }) {
  const rates = (context.rates ?? []).filter(
    (r) => r.code === "USD" || r.code === "EUR",
  );
  const now = context.weather_now;
  if (rates.length === 0 && !now) return null;

  return (
    <span className="hidden sm:inline">
      {rates.map((r) => (
        <span key={r.code}>
          {" · "}
          {r.code} <span className="jv-num text-fg">{rate2(r.rate_uah)}</span>
        </span>
      ))}
      {now && (
        <span>
          {" · "}
          {context.city}{" "}
          <span className="jv-num text-fg">{temp(now.temp_c)}</span>,{" "}
          {now.description_ua.toLowerCase()}
        </span>
      )}
    </span>
  );
}

const rate2 = (v: number): string =>
  v.toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const temp = (v: number): string => `${v > 0 ? "+" : ""}${Math.round(v)}°`;

/**
 * Loading placeholder shaped like this page: the hero trio and four state
 * cards. `OverviewPage.parity.test.tsx` keeps this in step with
 * `OverviewContent` — update both together. (The context line lives in the
 * page header now, which paints before the data does — nothing to fake here.)
 */
export function OverviewSkeleton() {
  return (
    <div
      className="space-y-5 sm:space-y-6"
      role="status"
      aria-busy="true"
      aria-label="Завантаження"
    >
      <div className="grid gap-5 sm:gap-6 lg:grid-cols-12 lg:items-stretch">
        {/* «Задачі на сьогодні» — the TodoPanel card. */}
        <div className="jv-card space-y-2.5 p-3 sm:p-4 lg:col-span-4">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-9/12" />
          <Skeleton className="h-4 w-10/12" />
          <Skeleton className="h-8 w-full" />
        </div>
        {/* «Мій день» — the hour grid, which is what makes this row tall. */}
        {/* Flex column like the real card, so the grid filler below can take
            its height from the row rather than set it. */}
        <div className="jv-card flex flex-col gap-3 p-3 sm:p-4 lg:col-span-5">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-44" />
          </div>
          {/* Mirrors the real scroller: a `max-h-80` window onto the full
              `GRID_TO_MIN - GRID_FROM_MIN` hours on a phone, and on the desktop
              a `basis-0` filler that takes the row's height from the other two
              cards instead of dictating it. */}
          <Skeleton
            className="max-h-80 w-full lg:max-h-none lg:min-h-64 lg:flex-1 lg:basis-0"
            style={{ height: ((GRID_TO_MIN - GRID_FROM_MIN) / 60) * HOUR_PX }}
          />
        </div>
        {/* «Найближчі події» — five rows and the calendar door. Hidden below
            `lg` exactly as the real column is, so the phone skeleton promises
            two cards and the phone delivers two. */}
        <div className="jv-card hidden lg:flex lg:col-span-3 flex-col space-y-2.5 p-3 sm:p-4">
          <Skeleton className="h-4 w-2/3" />
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-8 w-full" />
          ))}
          <Skeleton className="h-7 w-full" />
        </div>
      </div>

      {/* Гроші (bar + charges), Бізнес (the bot's pulse), Здоровʼя (four rows),
          Цілі (three rows) — the row counts are what each card actually draws,
          so nothing jumps when the data lands. */}
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-6 xl:grid-cols-4">
        {[3, 1, 4, 3].map((rows, i) => (
          <div key={i} className="jv-card space-y-2.5 p-3 sm:p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-3 w-32" />
            <div className="space-y-1.5 pt-1">
              {Array.from({ length: rows }, (_, row) => (
                <Skeleton key={row} className="h-3 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SECTION_UA: Record<SectionStatus["section"], string> = {
  money: "Фінанси",
  business: "Бізнес",
  goals: "Цілі",
  context: "Контекст",
  intel: "Аналітика",
};

/**
 * Which connector is behind a source id, in words he would use.
 *
 * The 2026-08-21 call was to keep plumbing out of the banner; the 2026-09-07
 * one reversed it for this banner alone. «Один блок показує не найсвіжіші
 * дані» told him nothing he could act on — naming Monobank tells him which app
 * to open. Only the four ids `/api/summary` can actually emit are listed
 * (`routes_summary.py`: monobank, lovytalon, postgres, context); a map
 * that pretended to cover Todoist or Hevy would be documentation of a fan-out
 * that does not exist. Anything else falls through as its own id rather than
 * being swallowed.
 */
const SOURCE_UA: Record<string, string> = {
  monobank: "Monobank",
  lovytalon: "LovyTalon",
  postgres: "База даних",
  context: "Зовнішні дані",
};

const connectorName = (source: string): string => SOURCE_UA[source] ?? source;

/**
 * «2 год» / «15 хв» — how long ago a source last answered, or null when that
 * cannot be said. Under a minute counts as now and gets no parenthesis: «щойно
 * оновлено» under a staleness warning is a contradiction.
 */
function agoLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const mins = Math.floor((Date.now() - at) / 60_000);
  if (mins < 1) return null;
  if (mins < 60) return `${mins} хв`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours} год` : `${Math.floor(hours / 24)} дн.`;
}

/**
 * The connector worth naming for this block, or null.
 *
 * Null in two cases. An empty `source` has nothing to name. And a source whose
 * id *is* the section — `context` under «Контекст» — would only say the same
 * word twice: «Контекст: Зовнішні дані…» is a tautology, and the sentence
 * without it loses nothing.
 */
function connectorNote(section: SectionStatus): string | null {
  if (!section.source || section.source === section.section) return null;
  return connectorName(section.source);
}

/**
 * «Фінанси: дані не найсвіжіші (Monobank, оновлено 2 год тому)».
 *
 * The connector is a parenthetical label, never the subject of a verb. The map
 * holds both singulars («Monobank», «База даних») and a plural («Зовнішні
 * дані»), and no single Ukrainian sentence agrees with both — «Зовнішні дані
 * показує…» is how the first draft of this banner read. Whatever grammar the
 * line needs it takes from `дані` (plural) and `джерело` (neuter singular),
 * which are fixed words of the template.
 */
function sectionLine(section: SectionStatus): string {
  const ago = agoLabel(section.fetched_at);
  const notes = [connectorNote(section), ago && `оновлено ${ago} тому`].filter(
    (note): note is string => Boolean(note),
  );
  const tail = notes.length > 0 ? ` (${notes.join(", ")})` : "";
  const name = SECTION_UA[section.section];
  return section.status === "error"
    ? `${name}: джерело не відповідає${tail} — показано збережені дані`
    : `${name}: дані не найсвіжіші${tail}`;
}

/**
 * Honesty banner: which block is stale, which connector made it stale, and how
 * long ago that connector last spoke. No «Детальніше» link — there is no page
 * behind it that would say more than this line already does (Vitalik,
 * 2026-09-07). `degraded_sources` feeds the fallback when a payload carries no
 * section detail.
 */
function DegradedBanner({ data }: { data: DashboardSummary }) {
  // Defensive: a summary payload without these arrays must degrade to "no
  // banner", never throw and blank the entire home screen.
  const failing = (data.sections ?? []).filter((s) => s.status !== "ok");
  const degraded = data.degraded_sources ?? [];
  if (failing.length === 0 && degraded.length === 0) return null;

  const worst = failing.some((s) => s.status === "error") ? "danger" : "warn";
  const lines =
    failing.length > 0
      ? failing.map((s) => ({ key: s.section, text: sectionLine(s) }))
      : degraded.map((source) => ({
          key: source,
          // Same reason as `sectionLine`: the name is a label in parentheses,
          // so it can be plural without dragging a verb out of agreement.
          text: `Дані не найсвіжіші (${connectorName(source)})`,
        }));

  return (
    // Tinted, not outlined — the same recipe `ErrorBanner` uses, and the two
    // stack in the same `banner` slot: one bordered box next to one tinted wash
    // read as two different kinds of warning.
    <div
      role="status"
      className={`rounded-card px-3 py-2.5 text-xs sm:px-4 ${
        worst === "danger" ? "bg-danger/8" : "bg-warn/8"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusDot tone={worst} />
        <span
          className={`font-medium ${worst === "danger" ? "text-danger" : "text-warn"}`}
        >
          Часткові дані
        </span>
        {/* The common case is one stale block, and it belongs on the same line
            as the label — a one-item list is a list for no one. */}
        {lines.length === 1 && (
          <span className="text-muted">{lines[0].text}</span>
        )}
      </div>
      {lines.length > 1 && (
        <ul className="mt-1 space-y-0.5 text-muted">
          {lines.map((line) => (
            <li key={line.key}>{line.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
